import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Box,
    LinearProgress,
    Divider
} from '@mui/material';
import {
    Edit as EditIcon,
    BarChart as BarChartIcon,
    History as HistoryIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
    PlayArrow as StartIcon,
    FileCopy as FileCopyIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    Send as SendIcon,
    Share as ShareIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../../hooks/useAuth';

const CampaignCard = ({
                          campaign,
                          statusColors,
                          onEdit,
                          onViewHistory,
                          onViewContactStatuses,
                          onPause,
                          onStop,
                          onStart,
                          onCopy,
                          onDelete,
                          onSendTest,
                          onShare,
                          onViewStats
                          }) => {
    const { user } = useAuth();
    const userTimezone = user?.timezone || 'UTC+3';

    const formatWorkingHours = (start, end) => {
        if (start === undefined || end === undefined) return null;
        return `${String(start).padStart(2, '0')}:00 - ${String(end).padStart(2, '0')}:00`;
    };

    const workingHours = formatWorkingHours(campaign.working_hours_start, campaign.working_hours_end);

    // ОБНОВЛЕННАЯ функция - использует новые поля с бэкенда
    const getCampaignProgressInfo = () => {
        // Используем данные с бэкенда если доступны
        if (campaign.contacts_total !== undefined && campaign.sent_count !== undefined) {
            return {
                sent: campaign.sent_count,
                total: campaign.contacts_total,
                percentage: campaign.progress || 0
            };
        }

        // Fallback для старых данных
        const totalContacts = campaign.contactGroups?.length || 0;
        if (campaign.progress !== undefined && totalContacts > 0) {
            const sent = Math.round((campaign.progress / 100) * totalContacts);
            return {
                sent: sent,
                total: totalContacts,
                percentage: campaign.progress
            };
        }

        return {
            sent: 0,
            total: 0,
            percentage: 0
        };
    };

    // ОБНОВЛЕННАЯ функция - упрощенная версия
    const getInstancesInfo = () => {
        console.log('Campaign data for', campaign.name, ':', campaign);
        console.log('campaign.Instances:', campaign.Instances);

        // Используем данные с бэкенда если доступны
        if (campaign.Instances && Array.isArray(campaign.Instances)) {
            console.log('Using campaign.Instances:', campaign.Instances);
            return campaign.Instances.map(instance => ({
                id: instance.id,
                alias: instance.alias,
                phone: instance.phone,
                status: instance.status
            }));
        }

        console.log('No instances found for campaign:', campaign.name);
        return [];
    };

    // Функция для определения цвета бейджа по статусу инстанса
    const getInstanceBadgeColor = (status) => {
        const activeStatuses = ['connected', 'active', 'authorized', 'running'];
        const blockedStatuses = ['blocked', 'banned', 'suspended', 'unauthorized'];

        if (activeStatuses.includes(status?.toLowerCase())) {
            return '#4caf50'; // зеленый для активных
        } else if (blockedStatuses.includes(status?.toLowerCase())) {
            return '#f44336'; // красный для заблокированных и неавторизованных
        } else {
            return '#ff9800'; // оранжевый для остальных статусов
        }
    };

    const progressInfo = getCampaignProgressInfo(); // Обновляем вызов функции
    const instancesInfo = getInstancesInfo();

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="h2" noWrap>
                        {campaign.name}
                    </Typography>
                    <Chip
                        label={campaign.status}
                        color={statusColors[campaign.status]}
                        size="small"
                    />
                </Box>

                {campaign.startDate && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Start: {format(campaign.startDate, 'dd.MM.yyyy HH:mm')}
                    </Typography>
                )}

                {workingHours && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" component="span">
                            Working hours: {workingHours}
                        </Typography>
                        <Chip
                            label={userTimezone}
                            size="small"
                            variant="outlined"
                            sx={{ height: '18px', fontSize: '0.75rem' }}
                        />
                    </Box>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Sending delay: {campaign.sending_delay || 0} sec.
                </Typography>


                {/* Информация об инстансах */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                        Instances:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {instancesInfo.length > 0 ? (
                            instancesInfo.map((instance, index) => (
                                <Chip
                                    key={instance.id || index}
                                    label={instance.phone || instance.alias || `#${index + 1}`}
                                    size="small"
                                    title={`Instance ${instance.alias || instance.id}: ${instance.status || 'unknown'} ${instance.phone ? `(${instance.phone})` : ''}`}
                                    sx={{
                                        backgroundColor: getInstanceBadgeColor(instance.status),
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        height: '20px',
                                        '& .MuiChip-label': { px: 0.75 }
                                    }}
                                />
                            ))
                        ) : (
                            <Chip
                                label="No instances"
                                size="small"
                                sx={{
                                    backgroundColor: '#9e9e9e',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    height: '20px',
                                    '& .MuiChip-label': { px: 0.75 }
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contacts: {progressInfo.total}
                </Typography>

                {/* Прогресс */}

                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Progress
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                {progressInfo.sent} / {progressInfo.total}
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progressInfo.percentage}
                            sx={{ height: 8, borderRadius: 5 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {Math.round(progressInfo.percentage)}%
                            </Typography>
                        </Box>

                        {/* Дополнительная статистика */}
                        {(campaign.failed_count > 0 || campaign.pending_count > 0) && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                                {campaign.pending_count > 0 && (
                                    <Chip
                                        label={`Pending: ${campaign.pending_count}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#ff9800',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '18px'
                                        }}
                                    />
                                )}
                                {campaign.failed_count > 0 && (
                                    <Chip
                                        label={`Failed: ${campaign.failed_count}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '18px'
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>


                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onEdit(campaign)}
                            title={campaign.status === 'active' ? "View (cannot edit while campaign is running)" : "Edit"}
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onViewStats && onViewStats(campaign)}
                            title="Campaign stats"
                        >
                            <BarChartIcon />
                        </IconButton>
                        <IconButton
                            color="info"
                            size="small"
                            onClick={() => onViewHistory(campaign)}
                            title="View History"
                        >
                            <HistoryIcon />
                        </IconButton>
                        <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => onViewContactStatuses(campaign)}
                            title="Manage Campaign Contacts"
                        >
                            <PersonIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onCopy(campaign)}
                            title="Copy Campaign"
                        >
                            <FileCopyIcon />
                        </IconButton>
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => onDelete(campaign)}
                            title={campaign.status === 'active' ? "Cannot delete while campaign is running" : "Delete"}
                            disabled={campaign.status === 'active'}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                    <Box>
                        <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => onShare && onShare(campaign)}
                            title="Share public link"
                        >
                            <ShareIcon />
                        </IconButton>
                        <IconButton
                            color="info"
                            size="small"
                            onClick={() => onSendTest(campaign)}
                            title="Send test message"
                        >
                            <SendIcon />
                        </IconButton>
                        {(campaign.status === 'draft' || campaign.status === 'paused') && (
                            <IconButton
                                color="success"
                                size="small"
                                onClick={() => onStart(campaign)}
                                title="Start Campaign"
                            >
                                <StartIcon />
                            </IconButton>
                        )}
                        {campaign.status === 'active' && (
                            <IconButton
                                color="warning"
                                size="small"
                                onClick={() => onPause(campaign)}
                                title="Pause Campaign"
                            >
                                <PauseIcon />
                            </IconButton>
                        )}
                        {(campaign.status === 'active' || campaign.status === 'paused') && (
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => onStop(campaign)}
                                title="Stop Campaign"
                            >
                                <StopIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CampaignCard;